import type { A1Summary, RunSession } from '../../types/ski-data';
import StatusBadge from '../common/StatusBadge';

/**
 * RunOverview - Top 120px of the R2 left column.
 *
 * Redesigned as a "score story": a large overall score with a color-coded
 * level badge, a coach comment sentence tuned to the score band, and a
 * trend indicator. The eight A1 basic metrics (distance, duration, max/avg
 * speed, slope, turn count, altitude drop, max g-force) are tucked into a
 * collapsible <details> element so the headline story stays compact; the
 * grid expands below the 120px area when opened.
 *
 * RunOverview - R2 左栏顶部 120px 区域。
 * 重新设计为"评分故事"：大号总分配等级徽章，下方为按分数段生成的教练评语
 * 与趋势提示。A1 基础八项指标（距离、时长、最大/平均速度、坡度、弯数、落差、
 * 最大 G 力）收纳进可折叠的 <details> 元素，使顶部评分故事保持紧凑；
 * 展开后网格向 120px 区域下方扩展。
 */
interface RunOverviewProps {
  runSession: RunSession;
}

interface MetricDef {
  key: keyof A1Summary;
  label: string;
  unit: string;
  format: (v: number) => string;
}

const METRICS: MetricDef[] = [
  { key: 'total_distance', label: '距离', unit: 'm', format: (v) => v.toFixed(0) },
  { key: 'total_duration', label: '时长', unit: 's', format: (v) => v.toFixed(1) },
  { key: 'max_speed', label: '最大速度', unit: 'm/s', format: (v) => v.toFixed(1) },
  { key: 'avg_speed', label: '平均速度', unit: 'm/s', format: (v) => v.toFixed(1) },
  { key: 'slope_angle', label: '坡度', unit: '°', format: (v) => v.toFixed(0) },
  { key: 'turn_count', label: '弯数', unit: '', format: (v) => v.toFixed(0) },
  { key: 'altitude_drop', label: '落差', unit: 'm', format: (v) => v.toFixed(0) },
  { key: 'max_g_force', label: '最大G力', unit: 'g', format: (v) => v.toFixed(2) },
];

// Coach comment by score band / 按分数段给出教练评语
function getCoachComment(score: number): string {
  if (score >= 75) return '整体表现不错，继续保持';
  if (score >= 60) return '整体一般，主要短板在立刃和压力转移';
  return '整体偏弱，建议从外板压力转移开始练';
}

export default function RunOverview({ runSession }: RunOverviewProps) {
  const { overall_score: score, level, a1_summary } = runSession;

  return (
    <div
      style={{
        height: '120px',
        flexShrink: 0,
        padding: 'var(--space-sm) var(--space-md)',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-xs)',
        overflow: 'visible',
      }}
    >
      {/* Score + level badge / 分数 + 等级徽章 */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)' }}>
        <span
          style={{
            fontSize: 'var(--fs-2xl)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)',
            lineHeight: 1,
          }}
        >
          {score.toFixed(0)}
        </span>
        <StatusBadge level={level} size="sm" />
      </div>

      {/* Coach comment / 教练评语 */}
      <div
        style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-secondary)',
          lineHeight: 1.3,
        }}
      >
        {getCoachComment(score)}
      </div>

      {/* Trend indicator / 趋势提示 */}
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--status-good)' }}>
        比上次进步 5 分
      </div>

      {/* A1 basic metrics, collapsible / A1 基础八项指标，可折叠 */}
      <details style={{ marginTop: 'auto' }}>
        <summary
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            listStyle: 'none',
          }}
        >
          A1 基础八项指标
        </summary>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'var(--space-xs) var(--space-sm)',
            paddingTop: 'var(--space-xs)',
          }}
        >
          {METRICS.map((m) => (
            <div key={m.key} style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-tertiary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: 'var(--fs-sm)',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}
              >
                {m.format(a1_summary[m.key])}
                {m.unit}
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
